// src/pages/MapPage.js
import React, { useEffect, useRef, useState } from 'react';
import { Box, Drawer, Button, TextField, FormControl, InputLabel, Select, MenuItem, Typography, Stack } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import * as turf from '@turf/turf';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const MapPage = ({ centerCoords }) => {
  const mapRef = useRef(null);
  const polygonRef = useRef(null);
  const waypointsRef = useRef([]);
  const linesRef = useRef([]);
  const layerGroupsRef = useRef({});
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [distance, setDistance] = useState(10);
  const [altitude, setAltitude] = useState(60);
  const [angle, setAngle] = useState(-45);
  const [oblique, setOblique] = useState('none');
  const [pattern, setPattern] = useState('horizontal');

  // Pan to new coords from nav bar address search
  useEffect(() => {
if (centerCoords?.lat && centerCoords?.lon && mapRef.current) {
  mapRef.current.setView([centerCoords.lat, centerCoords.lon], 18);
}

  }, [centerCoords]);

  useEffect(() => {
    if (mapRef.current) return;

    const lat = 40.1;
    const lon = -75.1;
    const radius = 100000;
    const apiUrl = `/API/check-airspacev2.php?lat=${lat}&lon=${lon}&radius=${radius}&type=all`;

    const colors = {
      asp: 'blue', obs: 'red', raa: 'orange', apt: 'purple',
      nav: 'green', hgl: 'brown', hot: 'pink', rca: 'cyan'
    };

    const map = L.map('map', { center: [lat, lon], zoom: 16 });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '© OpenStreetMap'
    }).addTo(map);

    fetch(apiUrl)
      .then(res => res.json())
      .then(data => {
        for (const [key, geojsonRaw] of Object.entries(data)) {
          const geojson = (geojsonRaw && geojsonRaw.features) ? geojsonRaw : { type: 'FeatureCollection', features: [] };
          const layer = L.geoJSON(geojson, {
            style: { color: colors[key] || 'black', weight: 2, opacity: 0.8 },
            pointToLayer: (feature, latlng) => L.circleMarker(latlng, {
              radius: 5, fillColor: colors[key] || 'black', color: '#000', weight: 1, opacity: 1, fillOpacity: 0.8
            }),
            onEachFeature: (feature, layer) => {
              const props = feature.properties || {};
              let popupContent = `<strong>${key.toUpperCase()}</strong><br>`;
              for (const [prop, value] of Object.entries(props)) {
                popupContent += `<b>${prop}</b>: ${value}<br>`;
              }
              layer.bindPopup(popupContent);
            }
          });
          const labels = {
  asp: 'Airspace',
  obs: 'Obstacles',
  raa: 'Restricted Areas',
  apt: 'Airports',
  nav: 'Navigation Aids',
  hgl: 'Hang Gliding Sites',
  hot: 'Hot Zones',
  rca: 'Restricted Corridors'
};
const label = labels[key] || key.toUpperCase();
layerGroupsRef.current[label] = layer;

        }
        L.control.layers(null, layerGroupsRef.current).addTo(map);
        Object.values(layerGroupsRef.current).forEach(layer => layer.addTo(map));
      });

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    const drawControl = new L.Control.Draw({
      draw: { polyline: false, circle: false, marker: false, rectangle: false, circlemarker: false, polygon: { allowIntersection: false, showArea: true, shapeOptions: { color: 'purple' } } },
      edit: { featureGroup: drawnItems }
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (event) => {
      const { layer } = event;
      drawnItems.clearLayers();
      drawnItems.addLayer(layer);
      polygonRef.current = layer;
      console.log('Polygon saved:', layer.getLatLngs());
      setDrawerOpen(true);
    });
  }, []);

  const clearWaypoints = () => {
    waypointsRef.current.forEach(m => m.remove());
    linesRef.current.forEach(l => l.remove());
    waypointsRef.current = [];
    linesRef.current = [];
  };
const clearPolygons = () => {
  if (polygonRef.current) {
    polygonRef.current.remove(); // remove from map
    polygonRef.current = null;   // reset reference
  }
};

  const generateWaypoints = () => {
    if (!polygonRef.current) return alert("Draw a polygon first.");
    clearWaypoints();

    const latlngsRaw = polygonRef.current.getLatLngs();
	const latlngs = Array.isArray(latlngsRaw[0]) ? latlngsRaw[0] : latlngsRaw;

    const bounds = L.latLngBounds(latlngs);
    const step = distance * 0.0000089;

    const minLat = bounds.getSouthWest().lat;
    const maxLat = bounds.getNorthEast().lat;
    const minLng = bounds.getSouthWest().lng;
    const maxLng = bounds.getNorthEast().lng;

    const polyCoords = latlngs.map(ll => [ll.lng, ll.lat]); 
    polyCoords.push([latlngs[0].lng, latlngs[0].lat]); // close polygon

    const poly = turf.polygon([[...polyCoords]]);
    const isInside = (pos) => {
      const pt = turf.point([pos.lng, pos.lat]);
      return turf.booleanPointInPolygon(pt, poly);
    };

    const addMarkers = (path, camAngle) => {
      path.forEach((pos, i) => {
        const marker = L.marker(pos, { title: `WP${waypointsRef.current.length + 1}` }).bindTooltip(`WP${waypointsRef.current.length + 1}`).addTo(mapRef.current);
        marker.altitude = altitude;
        marker.angle = camAngle;
        waypointsRef.current.push(marker);
        if (i > 0) {
          const line = L.polyline([path[i - 1], pos], { color: 'red' }).addTo(mapRef.current);
          linesRef.current.push(line);
        }
      });
    };

    const buildLine = (isHorizontal, offset) => {
      const row = [];
      for (let i = (isHorizontal ? minLng : minLat); i <= (isHorizontal ? maxLng : maxLat); i += step) {
        const lat = isHorizontal ? offset : i;
        const lng = isHorizontal ? i : offset;
        const pos = L.latLng(lat, lng);
        if (isInside(pos)) row.push(pos);
      }
      return row;
    };

    const patternAdd = (horizontal, addAll) => {
      let pass = 0;
      const outerMin = horizontal ? minLat : minLng;
      const outerMax = horizontal ? maxLat : maxLng;
      for (let o = outerMin; o <= outerMax; o += step) {
        const row = buildLine(horizontal, o);
        if (row.length >= 2) {
          if (pass % 2) row.reverse();
          addMarkers(addAll ? row : [row[0], row[row.length - 1]], angle);
          pass++;
        }
      }
    };

    const angles = [angle];
    if (oblique === 'one') angles.push(-45);
    if (oblique === 'dual') angles.push(-45, -20);

angles.forEach(camAngle => {
  if (pattern === 'horizontal') patternAdd(true, false);
  if (pattern === 'vertical') patternAdd(false, false);
  if (pattern === 'point') patternAdd(true, true);
  if (pattern === 'grid') {
    patternAdd(true, false);
    patternAdd(false, false);
  }
  if (pattern === 'spiral') {
    const center = bounds.getCenter();
    let a = 0, r = step;
    const maxR = Math.max(maxLat - minLat, maxLng - minLng);
    while (r < maxR) {
      const lat = center.lat + r * Math.cos(a);
      const lng = center.lng + r * Math.sin(a);
      const pos = L.latLng(lat, lng);
      if (isInside(pos)) addMarkers([pos], camAngle);
      a += Math.PI / 8;
      r += step / 10;
    }
  }
});

// ✅ After all waypoints are added — draw full flight path
if (waypointsRef.current.length > 1) {
  const flightPath = L.polyline(
    waypointsRef.current.map(m => m.getLatLng()),
    { color: 'red', weight: 2, opacity: 0.8 }
  ).addTo(mapRef.current);
  linesRef.current.push(flightPath); // so it clears later
}

  };

  const exportData = (type) => {
    if (!waypointsRef.current.length) return alert("No waypoints to export.");
    if (type === 'csv') {
      let csv = 'WP#,Latitude,Longitude,Altitude\n';
      waypointsRef.current.forEach((m, i) => {
        csv += `${i + 1},${m.getLatLng().lat},${m.getLatLng().lng},${m.altitude}\n`;
      });
      downloadFile(csv, 'waypoints.csv', 'text/csv');
    }
    if (type === 'kml') {
      let kml = `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document>`;
      waypointsRef.current.forEach((m, i) => {
        kml += `\n<Placemark><name>WP${i + 1}</name><Point><coordinates>${m.getLatLng().lng},${m.getLatLng().lat},${m.altitude}</coordinates></Point></Placemark>`;
      });
      kml += `\n</Document></kml>`;
      downloadFile(kml, 'waypoints.kml', 'application/vnd.google-earth.kml+xml');
    }
    if (type === 'geojson') {
      const features = waypointsRef.current.map((m, i) => ({
        type: "Feature",
        properties: { name: `WP${i + 1}`, altitude: m.altitude },
        geometry: { type: "Point", coordinates: [m.getLatLng().lng, m.getLatLng().lat] }
      }));
      const geojson = { type: "FeatureCollection", features };
      downloadFile(JSON.stringify(geojson, null, 2), 'waypoints.json', 'application/json');
    }
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
<Box sx={{ mt: 2, px: 2, height: 'calc(100vh - 80px)', display: 'flex' }}>
  <Box sx={{ flexGrow: 1, position: 'relative' }}>
    <div id="map" style={{ height: '100%', width: '100%' }}></div>
  </Box>

  <Box
    sx={{
      width: 300,
      p: 2,
      bgcolor: 'background.paper',
      borderLeft: '1px solid #ccc',
      overflowY: 'auto'
    }}
  >
    <Typography variant="h6">Waypoint Planner</Typography>
    <Stack spacing={2} sx={{ mt: 2 }}>
      <TextField label="Zigzag Distance (m)" type="number" value={distance} onChange={e => setDistance(Number(e.target.value))} />
      <TextField label="Altitude (m)" type="number" value={altitude} onChange={e => setAltitude(Number(e.target.value))} />
      <TextField label="Camera Angle (°)" type="number" value={angle} onChange={e => setAngle(Number(e.target.value))} />
      <FormControl>
        <InputLabel>Oblique Passes</InputLabel>
        <Select value={oblique} onChange={e => setOblique(e.target.value)}>
          <MenuItem value="none">None</MenuItem>
          <MenuItem value="one">Single (-45°)</MenuItem>
          <MenuItem value="dual">Dual (-45°, -20°)</MenuItem>
        </Select>
      </FormControl>
      <FormControl>
        <InputLabel>Pattern</InputLabel>
        <Select value={pattern} onChange={e => setPattern(e.target.value)}>
          <MenuItem value="horizontal">Horizontal</MenuItem>
          <MenuItem value="vertical">Vertical</MenuItem>
          <MenuItem value="grid">Grid</MenuItem>
          <MenuItem value="spiral">Spiral</MenuItem>
          <MenuItem value="point">Point</MenuItem>
        </Select>
      </FormControl>
      <Button variant="contained" onClick={generateWaypoints}>Generate</Button>
      <Button variant="outlined" onClick={() => exportData('csv')}>Export CSV</Button>
      <Button variant="outlined" onClick={() => exportData('kml')}>Export KML</Button>
      <Button variant="outlined" onClick={() => exportData('geojson')}>Export GeoJSON</Button>
      <Button variant="text" color="error" onClick={clearWaypoints}>Clear Waypoints</Button>
	  <Button variant="text" color="error" onClick={clearPolygons}>Clear Polygons</Button>
            <TextField label="Zigzag Distance (m)" type="number" value={distance} onChange={e => setDistance(Number(e.target.value))} />
            <TextField label="Altitude (m)" type="number" value={altitude} onChange={e => setAltitude(Number(e.target.value))} />
            <TextField label="Camera Angle (°)" type="number" value={angle} onChange={e => setAngle(Number(e.target.value))} />
            <FormControl>
              <InputLabel>Oblique Passes</InputLabel>
              <Select value={oblique} onChange={e => setOblique(e.target.value)}>
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="one">Single (-45°)</MenuItem>
                <MenuItem value="dual">Dual (-45°, -20°)</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Pattern</InputLabel>
              <Select value={pattern} onChange={e => setPattern(e.target.value)}>
                <MenuItem value="horizontal">Horizontal</MenuItem>
                <MenuItem value="vertical">Vertical</MenuItem>
                <MenuItem value="grid">Grid</MenuItem>
                <MenuItem value="spiral">Spiral</MenuItem>
                <MenuItem value="point">Point</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={generateWaypoints}>Generate</Button>
            <Button variant="outlined" onClick={() => exportData('csv')}>Export CSV</Button>
            <Button variant="outlined" onClick={() => exportData('kml')}>Export KML</Button>
            <Button variant="outlined" onClick={() => exportData('geojson')}>Export GeoJSON</Button>
            <Button variant="text" color="error" onClick={clearWaypoints}>Clear WPs</Button>
          </Stack>
        </Box>
      
    </Box>
  );
};

export default MapPage;
